import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useSubscription } from '@/contexts/SubscriptionContext';
import { useVoiceOutput } from '@/hooks/useVoiceOutput';
import { Message, SessionSummary } from './types';
import { RecommendedTool } from '../RecommendedTools';
import { createSSELineBuffer, processSSEChunk, flushAndProcessSSE } from '@/lib/sseParser';

interface UseTherapyChatProps {
  userId: string;
  selectedMode: string;
  initialContext?: string;
}

export function useTherapyChat({ userId, selectedMode, initialContext }: UseTherapyChatProps) {
  const { totalCredits, checkCredits, isTrialActive, hasAITherappiAccess } = useSubscription();
  const [messages, setMessages] = useState<Message[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isStreaming, setIsStreaming] = useState(false);
  const [displayName, setDisplayName] = useState<string>('');
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [sessionGoal, setSessionGoal] = useState<string>('');
  const [rMethodStep, setRMethodStep] = useState(0);
  const [savedInsights, setSavedInsights] = useState<string[]>([]);
  const [sessionSummary, setSessionSummary] = useState<SessionSummary | null>(null);
  const [showSummaryDialog, setShowSummaryDialog] = useState(false);
  const [recommendedTools, setRecommendedTools] = useState<RecommendedTool[]>([]);
  const [voiceMode, setVoiceMode] = useState(() => {
    const saved = localStorage.getItem('therappi_voice_mode');
    return saved === 'true';
  });
  
  const { speak: speakWithVoice, stop: stopVoice, isSpeaking: isVoiceSpeaking, isGenerating: isGeneratingVoice } = useVoiceOutput();

  // Fetch profile
  useEffect(() => {
    const fetchProfile = async () => {
      const { data } = await supabase
        .from('profiles')
        .select('display_name')
        .eq('user_id', userId)
        .single();
      
      if (data?.display_name) {
        setDisplayName(data.display_name.split(' ')[0]);
      }
    };
    fetchProfile();
  }, [userId]);

  // Persist voice mode
  useEffect(() => {
    localStorage.setItem('therappi_voice_mode', voiceMode.toString());
  }, [voiceMode]);

  // Load conversation history
  useEffect(() => {
    const loadConversationHistory = async () => {
      try {
        const { data: conversations } = await supabase
          .from('therapy_conversations')
          .select('id')
          .eq('user_id', userId)
          .order('last_message_at', { ascending: false })
          .limit(1);

        let convId = null;
        if (conversations && conversations.length > 0) {
          convId = conversations[0].id;
          setConversationId(convId);

          const { data: messageData } = await supabase
            .from('therapy_messages')
            .select('role, content, created_at')
            .eq('conversation_id', convId)
            .order('created_at', { ascending: true });

          if (messageData && messageData.length > 0) {
            const loadedMessages = messageData.map((msg: any, index: number) => ({
              id: `${index}`,
              text: msg.content,
              isUser: msg.role === 'user',
              timestamp: new Date(msg.created_at),
              dbId: msg.id
            }));
            
            setMessages([...loadedMessages]);
            sessionStorage.setItem('therappi_returning_user', 'true');
            setIsLoading(false);
            return;
          }
        }

        const greeting = displayName ? `Hello ${displayName}` : "Hello";
        setMessages([{
          id: '1',
          text: `${greeting}, I'm Therappi®. I'm here to listen and support you. How are you feeling today?`,
          isUser: false,
          timestamp: new Date()
        }]);
        sessionStorage.removeItem('therappi_returning_user');
      } catch {
        const greeting = displayName ? `Hello ${displayName}` : "Hello";
        setMessages([{
          id: '1',
          text: `${greeting}, I'm Therappi®. I'm here to listen and support you. How are you feeling today?`,
          isUser: false,
          timestamp: new Date()
        }]);
      } finally {
        setIsLoading(false);
      }
    };

    loadConversationHistory();
  }, [userId, displayName]);

  // Detect Soulprint Sequence™ step
  useEffect(() => {
    if (selectedMode === 'qsp' && messages.length > 0) {
      const lastMessage = messages[messages.length - 1];
      if (!lastMessage.isUser) {
        const text = lastMessage.text.toLowerCase();
        if (text.includes('recognise') || text.includes('recognise')) setRMethodStep(0);
        else if (text.includes('reflect')) setRMethodStep(1);
        else if (text.includes('release')) setRMethodStep(2);
        else if (text.includes('reprogramme') || text.includes('reprogram')) setRMethodStep(3);
      }
    }
  }, [messages, selectedMode]);

  const parseToolRecommendations = (text: string) => {
    const tools: RecommendedTool[] = [];
    const pathMatches = text.match(/\/(sanctuary|crisis|meditations|manifestation|telempathy-tools|daily-check-in|sleep-tracker|alcohol-tracker|cycle-tracker|future-self|whoop-dashboard)([\w\-\/]*)/gi);
    
    if (pathMatches && pathMatches.length > 0) {
      const uniquePaths = [...new Set(pathMatches)];
      
      uniquePaths.forEach(path => {
        const lowerText = text.toLowerCase();
        const pathIndex = lowerText.indexOf(path.toLowerCase());
        const contextStart = Math.max(0, pathIndex - 100);
        const contextEnd = Math.min(text.length, pathIndex + path.length + 100);
        const context = text.substring(contextStart, contextEnd);
        const sentences = context.split(/[.!?]\s/);
        const relevantSentence = sentences.find(s => s.toLowerCase().includes(path.toLowerCase())) || '';
        
        const isPaid = lowerText.includes('premium') || lowerText.includes('subscription') || 
                      path.includes('telempathy') || path.includes('whoop');
        
        let category = 'Tool';
        if (path.includes('sanctuary')) category = 'Sanctuary';
        else if (path.includes('crisis')) category = 'Crisis';
        else if (path.includes('meditation')) category = 'Meditation';
        else if (path.includes('manifestation')) category = 'Manifestation';
        else if (path.includes('telempathy')) category = 'Telempathy';
        else if (path.includes('tracker') || path.includes('check-in')) category = 'Tracking';
        
        const pathParts = path.split('/').filter(Boolean);
        const toolName = pathParts[pathParts.length - 1]
          .split('-')
          .map(word => word.charAt(0).toUpperCase() + word.slice(1))
          .join(' ');
        
        tools.push({
          name: toolName || 'Recommended Tool',
          path: path,
          reason: relevantSentence.trim() || 'Recommended based on our conversation',
          isPaid,
          category
        });
      });
      
      setRecommendedTools(tools.slice(0, 3));
    }
  };

  const streamTherapyResponse = async (userMessage: string) => {
    setIsStreaming(true);
    try {
      const isReturningUser = sessionStorage.getItem('therappi_returning_user') === 'true';
      if (isReturningUser) {
        sessionStorage.removeItem('therappi_returning_user');
      }

      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      if (sessionError || !session) throw new Error('Not authenticated');

      const assistantMessageId = (Date.now() + 1).toString();
      setMessages(prev => [...prev, { id: assistantMessageId, text: '', isUser: false, timestamp: new Date() }]);

      const response = await fetch(
        `https://jbdsdzmyqkfbqijnoqmo.supabase.co/functions/v1/therapy-chat`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({ 
            message: userMessage, 
            conversationId, 
            userId, 
            mode: selectedMode,
            isReturningUser,
            additionalContext: initialContext
          })
        }
      );

      if (response.status === 429) {
        toast.error('Too many requests. Please wait a moment before trying again.');
        setMessages(prev => prev.filter(m => m.id !== assistantMessageId));
        return;
      }

      if (response.status === 402) {
        toast.error('Out of credits', {
          description: isTrialActive 
            ? 'Your trial credits are exhausted. Subscribe to continue with 300 monthly credits.'
            : 'Purchase more credits or subscribe to AI Therappi®.',
          action: {
            label: isTrialActive ? 'Subscribe' : 'Get Credits',
            onClick: () => window.location.href = isTrialActive ? '/subscription' : '/credits'
          },
          duration: 6000
        });
        setMessages(prev => prev.filter(m => m.id !== assistantMessageId));
        return;
      }

      if (!response.ok || !response.body) {
        setMessages(prev => prev.filter(m => m.id !== assistantMessageId));
        throw new Error('Failed to start stream');
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let assistantText = '';
      const sseBuf = createSSELineBuffer();
      let streamDone = false;

      while (!streamDone) {
        const { done, value } = await reader.read();
        if (done) {
          const final = flushAndProcessSSE(sseBuf);
          assistantText += final.texts.join('');
          if (final.conversationId && !conversationId) setConversationId(final.conversationId);
          break;
        }

        const { texts, done: isDone, conversationId: cId } = processSSEChunk(sseBuf, decoder.decode(value, { stream: true }));
        if (texts.length > 0) {
          assistantText += texts.join('');
          setMessages(prev => {
            const newMessages = [...prev];
            const lastMsg = newMessages[newMessages.length - 1];
            if (lastMsg && !lastMsg.isUser) lastMsg.text = assistantText;
            return newMessages;
          });
        }
        if (cId && !conversationId) {
          setConversationId(cId);
        }
        if (isDone) { streamDone = true; break; }
      }

      parseToolRecommendations(assistantText);
      
      if (voiceMode && assistantText.trim().length > 0) {
        await speakWithVoice(assistantText);
      }
    } catch {
      toast.error('Unable to connect to therapy AI. Please try again.');
      setMessages(prev => [...prev, { id: (Date.now() + 1).toString(), text: "I'm here to listen. Could you tell me more about how you're feeling?", isUser: false, timestamp: new Date() }]);
    } finally {
      setIsStreaming(false);
    }
  };

  const handleSendMessage = async (text: string) => {
    if (!text.trim() || isStreaming) return;
    
    if (totalCredits < 3) {
      if (isTrialActive) {
        toast.error("Your trial has no credits remaining", {
          description: "Subscribe to AI Therappi® to continue conversations with monthly credits included.",
          action: { label: "Subscribe Now", onClick: () => window.location.href = '/subscription' },
          duration: 6000
        });
      } else if (!hasAITherappiAccess()) {
        toast.error("No AI Therappi® access", {
          description: "Subscribe to AI Therappi® for 300 monthly credits or purchase a credit pack.",
          action: { label: "View Options", onClick: () => window.location.href = '/subscription' },
          duration: 6000
        });
      } else {
        toast.error("Out of credits", {
          description: "Purchase a credit pack to continue conversations.",
          action: { label: "Buy Credits", onClick: () => window.location.href = '/credits' },
          duration: 6000
        });
      }
      return;
    }

    const userMessage: Message = { id: Date.now().toString(), text: text.trim(), isUser: true, timestamp: new Date() };
    setMessages(prev => [...prev, userMessage]);
    await streamTherapyResponse(text.trim());
    await checkCredits();
  };

  const handleDeleteMessage = async (messageId: string, dbId?: string) => {
    if (!dbId) {
      setMessages(prev => prev.filter(m => m.id !== messageId));
      return;
    }

    try {
      const { error } = await supabase
        .from('therapy_messages')
        .delete()
        .eq('id', dbId)
        .eq('user_id', userId);

      if (error) throw error;
      setMessages(prev => prev.filter(m => m.id !== messageId));
      toast.success('Message deleted from conversation');
    } catch {
      toast.error('Failed to delete message');
    }
  };

  const handleDeleteConversation = async () => {
    if (!conversationId) return;

    try {
      const { error } = await supabase
        .from('therapy_conversations')
        .delete()
        .eq('id', conversationId)
        .eq('user_id', userId);

      if (error) throw error;

      setConversationId(null);
      const greeting = displayName ? `Hello ${displayName}` : "Hello";
      setMessages([{
        id: '1',
        text: `${greeting}, I'm Therappi®. I'm here to listen and support you. How are you feeling today?`,
        isUser: false,
        timestamp: new Date()
      }]);
      sessionStorage.removeItem('therappi_returning_user');
      toast.success('Conversation deleted');
    } catch {
      toast.error('Failed to delete conversation');
    }
  };

  const handleSetGoal = (goal: string) => {
    setSessionGoal(goal);
    if (goal) toast.success('Session goal set');
  };

  const handleSaveInsight = (messageText: string) => {
    setSavedInsights(prev => [...prev, messageText]);
    toast.success('Insight saved to this session');
  };

  const handleGenerateSummary = async () => {
    if (!conversationId || messages.length < 4) {
      toast.error('Need more conversation to generate summary');
      return;
    }

    try {
      const { data, error } = await supabase.functions.invoke('generate-session-summary', {
        body: { conversationId, mode: selectedMode, goal: sessionGoal }
      });

      if (error) throw error;

      setSessionSummary({
        goal: sessionGoal,
        keyInsights: data.summary.keyInsights || [],
        patterns: data.summary.patterns || [],
        nextSteps: data.summary.nextSteps || [],
        emotionalJourney: data.summary.emotionalJourney || ''
      });
      setShowSummaryDialog(true);
    } catch {
      toast.error('Failed to generate summary');
    }
  };

  const handleToolClick = async (path: string) => {
    if (conversationId) {
      await supabase.from('therapy_tools_tracking').insert({
        user_id: userId,
        tool_name: path.split('/').pop() || path,
        tool_path: path,
        recommended_at: new Date().toISOString(),
        recommended_for: 'AI recommendation from conversation',
        used: true,
        used_at: new Date().toISOString()
      });
    }
    toast.success('Opening recommended tool...');
  };

  const toggleVoiceMode = () => {
    setVoiceMode(prev => {
      if (prev) {
        stopVoice();
        toast.success('Voice mode disabled');
      } else {
        toast.success('Voice mode enabled');
      }
      return !prev;
    });
  };

  return {
    messages,
    isLoading,
    isStreaming,
    conversationId,
    sessionGoal,
    rMethodStep,
    savedInsights,
    sessionSummary,
    showSummaryDialog,
    setShowSummaryDialog,
    recommendedTools,
    voiceMode,
    totalCredits,
    isVoiceSpeaking,
    isGeneratingVoice,
    handleSendMessage,
    handleDeleteMessage,
    handleDeleteConversation,
    handleSetGoal,
    handleSaveInsight,
    handleGenerateSummary,
    handleToolClick,
    toggleVoiceMode,
    stopVoice
  };
}
